<template>
  <div ref="contentOrgEl" class="content-organization">
    <!-- plus free access callout -->
    <plus-free-access-callout v-if="userAuthStore.plusFreeAccess.enabled" />

    <search-header
      title="Browse Workout Videos"
      :has-tutorial="false"
      active="collections"
      filter-url="/videos"
      browse-url="/videos/browse"
      :show-tags="false"
      @tag-update="tagUpdate"
    >
      <template v-if="hasQueryParams && !isDefaultQueryParams" #customContentTags>
        <ul class="tag-actions">
          <li class="action clear">
            <icon-button
              type="button"
              icon="close"
              classes="clear"
              label="Reset"
              :reduce="false"
              :icons-outlined="false"
              @click-action="reset"
            />
          </li>
        </ul>
      </template>
    </search-header>

    <div ref="contentResultsEl" class="content-type-options">
      <search-filters
        :loading="page.loading"
        :show-keywords="false"
        :show-tags="false"
        entity-type="workout"
        @filter-update="filterUpdate"
      >
        <video-browse-filters @filter-update="filterUpdate" />
      </search-filters>

      <div class="content-results">
        <!-- error state, modal will also show -->
        <callout v-if="errorMsg" type="error">
          <div>{{ errorMsg }}</div>
        </callout>

        <!-- result top bar -->
        <div class="results-details">
          <search-sort
            :total="collectionData.length"
            result-label="Collections"
            :sortable="false"
          />
        </div>

        <!-- results -->
        <content-collections
          v-if="!errorMsg && (searchResults.loading || collectionData.length)"
          v-slot="{ collectionOrder }"
          :loading="searchResults.loading"
          :collection-total="collectionData.length"
        >
          <content-collection
            v-for="(collection, index) in collectionData"
            v-show="collection.cards.length"
            :key="collection.id"
            :title="collection.title"
            :fc-edit="!!collection.filterCollection"
            :fc-delete="!!collection.filterCollection"
            :fc-copy="!!collection.filterCollection"
            :fc-share="!!collection.filterCollection"
            :view-all="collection.url"
            :filter-collection="collection.filterCollection"
            :style="{ order: collectionOrder(index) }"
            @filter-collection-updated="handleFilterCollectionUpdated"
            @filter-collection-copied="handleFilterCollectionCopied"
            @filter-collection-deleted="handleFilterCollectionDeleted"
          >
            <video-card
              v-for="video in collection.cards"
              :key="video.id"
              :video="video"
              :custom-expander-key="`collection-${collection.id}`"
              :plus-pass="searchStore.plusPass"
              :cheapest-monthly-price="searchStore.cheapestMonthlyPrice"
            />
          </content-collection>
        </content-collections>
        <div v-else class="content-group -center -vs-small">
          <search-no-results button-label="Reset" @clear-all="reset" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Workout video collections view
 * - filter collections and actions for plus user
 * - fitness blender collections for free users
 * - view all takes to a filter-less view of the collection
 */
import SearchHeader from '@/components/search/search-header.vue'
import SearchFilters from '@/components/search/search-filters.vue'
import { useUserAuthStore } from '@/stores/user-auth'
import { computed, onBeforeMount, onBeforeUnmount, ref, shallowRef, watch } from 'vue'
import { useSearchStore } from '@/stores/search'
import { type LocationQueryValue, useRoute } from 'vue-router'
import type {
  DropdownFilter,
  DropdownFilterItem,
  InitCheckboxFilter,
  PlusPass,
  VideoContentCollection,
} from '@/types/search-types'
import { fbApi, isAbortError, isSearchParamsEqual, objToSearchParams } from '@/utils/http-api'
import VideoBrowseFilters from '@/components/videos/video-browse-filters.vue'
import { removeArraySyntax } from '@/utils/helpers'
import ContentCollections from '@/components/search/content-collections.vue'
import Callout from '@/components/notifications/callout.vue'
import ContentCollection from '@/components/search/content-collection.vue'
import VideoCard from '@/components/videos/video-card.vue'
import SearchNoResults from '@/components/search/search-no-results.vue'
import SearchSort from '@/components/search/search-sort.vue'
import { useSearchRoute } from '@/composables/search-route'
import IconButton from '@/components/buttons/icon-button.vue'
import PlusFreeAccessCallout from '@/components/notifications/plus-free-access-callout.vue'
import type { FilterCollection } from '@/types/model-types'

/** STATE **/
const route = useRoute()
const userAuthStore = useUserAuthStore()
const searchStore = useSearchStore()
const { contentResultsEl, contentOrgEl, scrollAndFocus, routeUpdate, tagUpdate, filterUpdate } =
  useSearchRoute()

const page = ref<{
  loading: boolean
  error: string
}>({
  loading: false,
  error: '',
})

const searchResults = ref<{
  loading: boolean
  error: string
}>({
  loading: false,
  error: '',
})

const collectionData = shallowRef<VideoContentCollection[]>([])

/** NON REACTIVE **/
let searchInitAbort: AbortController | null = null
let fetchAbort: AbortController | null = null

const checkboxFilters: Record<string, InitCheckboxFilter> = {
  fb: {
    label: 'FB Collections',
    name: 'fb',
    items: [
      { label: "Today's Top Ten", value: 'top_ten' },
      { label: 'Trending', value: 'trending' },
      { label: 'Community Favorites', value: 'community_favorites' },
    ],
  },
  membership: {
    label: 'Membership',
    name: 'membership',
    items: [
      { label: 'Free', value: 'free' },
      { label: 'Plus', value: 'plus' },
    ],
  },
  difficulty: {
    label: 'Difficulty',
    name: 'difficulty',
    items: [
      { label: 'Beginner', value: 'beginner' },
      { label: 'Intermediate', value: 'intermediate' },
      { label: 'Advanced', value: 'advanced' },
    ],
  },
  trainingtype: {
    label: 'Training Types',
    name: 'trainingtype',
    items: [
      { label: 'HIIT', value: 'hiit' },
      { label: 'Strength', value: 'strength' },
      { label: 'Cardiovascular', value: 'cardio' },
      { label: 'Low Impact', value: 'low_impact' },
    ],
  },
}

const dropdownFilters: Record<string, DropdownFilter> = {
  filtercollection: {
    label: 'My Collections',
    name: 'filtercollection',
    multiple: false,
    value: '',
    items: [], // populated by search init
  },
  specialtyprogram: {
    label: 'Specialty Programs',
    name: 'specialtyprogram',
    multiple: false,
    value: '',
    items: [], // populated by search init
  },
}

/** COMPUTED **/
const errorMsg = computed(() => page.value.error || searchResults.value.error)

const hasQueryParams = computed(() => Object.keys(route.query).length > 0)

const isDefaultQueryParams = computed(() => {
  // do not check / show cta if searchInit is loading
  if (page.value.loading) {
    return true
  }

  const defaultParams = defaultQueryParams()
  const routeParams = removeArraySyntax(route.query)

  // have to check each key, could use lodash isEqual but only dealing
  // a few arrays and string values
  return isSearchParamsEqual(routeParams, defaultParams)
})

/** WATCHERS **/
watch(
  () => route.query,
  () => {
    // resync state from url source of truth
    syncUrlToState({ init: false })
    fetchCollections()
  },
)

/** LIFECYCLE **/
onBeforeMount(async () => {
  // clear everything
  searchStore.$reset()

  // allow search init to finish first before fetching videos,
  // it will populate team members before syncing state from url
  await searchInit()

  // searchInit doesn't re-throw errors, so only continue if it set data
  // helps avoid users quickly toggling between filter and browse views since
  // this method is async
  if (!(searchStore.init.plusPass && searchStore.init.cheapestMonthlyPrice)) {
    return
  }

  // if route already has query string, use it
  if (hasQueryParams.value) {
    syncUrlToState({ init: true })
    fetchCollections()
    return
  }

  // otherwise if no query string, use defaults
  const customQueryParams = defaultQueryParams()
  syncUrlToState({ init: true, customQueryParams })
  fetchCollections(customQueryParams)
})

// cancel fetch request, especially if going to browse view as it will
// as it will use the search store
onBeforeUnmount(() => {
  if (searchInitAbort) {
    searchInitAbort.abort()
  }

  if (fetchAbort) {
    fetchAbort.abort()
  }
})

/** METHODS **/
function defaultQueryParams(): Record<string, string | string[]> {
  return {
    fb: ['top_ten', 'trending', 'community_favorites'],
    membership: ['free'],
    difficulty: ['beginner'],
    trainingtype: ['hiit'],
    specialtyprogram:
      Array.isArray(dropdownFilters.specialtyprogram.items) &&
      dropdownFilters.specialtyprogram.items.length
        ? dropdownFilters.specialtyprogram.items[0].value
        : '',
    filtercollection:
      Array.isArray(dropdownFilters.filtercollection.items) &&
      dropdownFilters.filtercollection.items.length
        ? dropdownFilters.filtercollection.items[0].value
        : '',
  }
}

/**
 * Update pinia based on url query params as the source of truth.
 * Clear out values and reset them based on the query params
 */
function syncUrlToState({
  init,
  customQueryParams,
}: {
  init: boolean
  customQueryParams?: Record<string, string | string[]>
}) {
  // removes [] and forces array for items with [] in var names
  let queryParams = removeArraySyntax(route.query)

  // override if passed in params
  if (customQueryParams) {
    queryParams = customQueryParams
  }

  // filters
  searchStore.setCheckboxFilters({
    checkboxFilters,
    queryStringFilters: queryParams,
    checkboxUrlParameterNames: searchStore.createIncludeExcludeParamMap(
      {
        fb: 'fb',
        membership: 'membership',
        difficulty: 'difficulty',
        trainingtype: 'trainingtype',
      },
      userAuthStore.features.VIDEO_FILTERS,
    ),
    init,
  })

  searchStore.setDropdownFilters({
    dropdownFilters,
    queryStringFilters: queryParams,
    dropdownUrlParameterNames: {
      filtercollection: 'filtercollection',
      specialtyprogram: 'specialtyprogram',
    },
    init,
  })

  // no range filters

  // no keywords or sort

  // rebuild tags last, after all pinia state is loaded
  searchStore.setInitialTags()
}

/**
 * Set to defaults
 */
async function reset() {
  await routeUpdate(defaultQueryParams())
  scrollAndFocus({ scrollTo: contentOrgEl, focusTitle: true })
}

/**
 * Update a single collection
 */
function handleFilterCollectionUpdated(filterCollection: FilterCollection) {
  // update drop down item label
  if (dropdownFilters) {
    searchStore.updateDropdownItemLabel({
      filterName: 'filtercollection',
      value: `${filterCollection.id}`,
      label: filterCollection.title,
    })
  }

  // update filter collection and collection header title
  if (collectionData.value) {
    collectionData.value = collectionData.value.map((collection) => {
      if (collection.filterCollection && collection.filterCollection.id === filterCollection.id) {
        collection.filterCollection = {
          ...filterCollection,
        }

        collection.title = filterCollection.title

        return collection
      }

      // no changes
      return collection
    })
  }
  // end updates
}

/**
 * Add a copied collection to the dropdown
 */
function handleFilterCollectionCopied(filterCollection: FilterCollection) {
  console.log('handleFilterCollectionCopied')
  if (dropdownFilters) {
    searchStore.addDropdownItem({
      filterName: 'filtercollection',
      value: `${filterCollection.id}`,
      label: filterCollection.title,
    })
  }
}

/**
 * Remove a collection
 */
function handleFilterCollectionDeleted(filterCollectionId: number) {
  // remove drop down item from list
  if (dropdownFilters) {
    searchStore.deleteDropdownItem({
      filterName: 'filtercollection',
      value: `${filterCollectionId}`,
    })
  }

  // remove from state
  if (collectionData.value) {
    collectionData.value = collectionData.value.filter(
      (collection) => collection.filterCollection?.id !== filterCollectionId,
    )
  }
}

async function fetchCollections(
  customQueryParams?: Record<string, string | string[] | LocationQueryValue | LocationQueryValue[]>,
) {
  // clear any error
  searchResults.value.error = ''
  searchResults.value.loading = true

  // treat route as source of truth unless cusotm params passed in for "default"
  let params: Record<string, string | string[] | LocationQueryValue | LocationQueryValue[]> =
    route.query || {}
  params = removeArraySyntax(params)

  if (customQueryParams) {
    params = customQueryParams
  }

  try {
    // cancel any previous fetch
    if (fetchAbort) {
      fetchAbort.abort()
    }

    // re-init abort controller
    fetchAbort = new AbortController()

    const res = await fbApi.get('/videos/collections', {
      searchParams: objToSearchParams(params),
      signal: fetchAbort.signal,
    })

    if (!res || !res.body || !res.body.data) {
      throw new Error(`Unable to fetch workouts, please refresh the page and try again`)
    }

    const {
      collections,
    }: {
      collections: VideoContentCollection[]
    } = res.body.data

    collectionData.value = collections

    // disable facets
    searchStore.updateFacets()
  } catch (err) {
    // skip if Aborted
    if (isAbortError(err)) {
      console.warn('fetchCollections aborted')
      return
    }

    // oops model will show
    console.error(err)
    searchResults.value.error = `Unable to fetch collections, please refresh the page and try again`
  } finally {
    searchResults.value.loading = false
  }
}

/**
 * No loading state, should happen first
 */
async function searchInit() {
  // clear any error and set both loading stats so skeleton cards show
  page.value.error = ''
  page.value.loading = true
  searchResults.value.loading = true

  try {
    searchInitAbort = new AbortController()

    const res = await fbApi.get('/videos/collections/search-init', {
      signal: searchInitAbort.signal,
    })

    if (!res || !res.body || !res.body.data) {
      throw new Error(`Unable to fetch workouts, please refresh the page and try again`)
    }

    const {
      filterCollections,
      specialtyPrograms,
      plusPass,
      cheapestMonthlyPrice,
    }: {
      filterCollections: { id: number; title: string }[]
      specialtyPrograms: { id: number; shortName: string; title: string }[]
      plusPass: PlusPass
      cheapestMonthlyPrice: string
    } = res.body.data
    searchStore.setSearchInit({
      plusPass,
      cheapestMonthlyPrice,
    })

    // add specialty programs dropdown items
    if (specialtyPrograms) {
      dropdownFilters.specialtyprogram.items = specialtyPrograms.map(
        (specialtyProgram): DropdownFilterItem => ({
          label: specialtyProgram.shortName,
          value: `${specialtyProgram.id}`, // force string
        }),
      )
    }

    // add filter collection items
    if (filterCollections) {
      dropdownFilters.filtercollection.items = filterCollections.map(
        (filterCollection): DropdownFilterItem => ({
          label: filterCollection.title,
          value: `${filterCollection.id}`, // force string
        }),
      )
    }
  } catch (err) {
    // skip if Aborted
    if (isAbortError(err)) {
      return
    }

    // oops model will show
    console.error(err)
    page.value.error = `Unable to initialize collections, please refresh the page and try again`
  } finally {
    page.value.loading = false
  }
}
</script>
